/**
 * Guidance strategies: how a taxonomy classifies outcome text and advises
 * authors at one framework level.
 *
 * `verbListStrategy` covers any taxonomy that is levels with verbs, from data
 * alone. A taxonomy that classifies some other way supplies its own strategy.
 *
 * @module taxonomy/strategy
 */

import { type ComponentType, createElement } from "react";

import { GuidancePanel, type GuidancePanelProps } from "@/components/ui/GuidancePanel";
import { normaliseText } from "@/lib/language-detect";
import { leadingVerbClause } from "@/lib/lo-verb-clause";

import type {
  Alignment,
  Emphasis,
  QualificationFramework,
  Taxonomy,
  TaxonomyLevel,
  VerbEntry,
} from "./types";

/** A recognised verb, by lemma, and every taxonomy level it can express. */
export interface VerbMatch {
  verb: string;
  levels: readonly TaxonomyLevel[];
}

/** How well a verb suits a framework level. Advisory, never a rule. */
export type VerbSuitability = "recommended" | "possible" | "unlikely";

export interface GuidanceStrategy {
  readonly taxonomy: Taxonomy;
  readonly framework: QualificationFramework;
  /** Every known verb anywhere in the text, in order of first appearance. */
  scan(text: string): VerbMatch[];
  /** The learner's action verbs: those in the outcome's leading clause. */
  classify(text: string): VerbMatch[];
  /** What a framework level emphasises, or null outside the framework. */
  emphasisFor(level: number): Emphasis | null;
  /** Verbs suited to a framework level, primary levels first, each once. */
  suggest(level: number): VerbEntry[];
  /**
   * A verb's fit at a framework level, judged across every taxonomy level it
   * sits at: any primary makes it recommended, else any secondary possible.
   * Null outside the framework or for an unknown verb.
   */
  suitability(lemma: string, level: number): VerbSuitability | null;
  /** Authoring guidance for this taxonomy at a framework level. */
  Panel: ComponentType<GuidancePanelProps>;
}

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A strategy for a taxonomy of levels with verb lists, aligned to one framework. */
export function verbListStrategy(
  taxonomy: Taxonomy,
  framework: QualificationFramework,
  alignment: Alignment,
): GuidanceStrategy {
  const index = new Map<string, { entry: VerbEntry; levels: TaxonomyLevel[] }>();
  taxonomy.levels.forEach((level) => {
    level.verbs.forEach((entry) => {
      [entry.lemma, ...(entry.aliases ?? [])].forEach((spelling) => {
        const key = spelling.toLowerCase();
        const known = index.get(key);
        index.set(key, { entry, levels: [...(known?.levels ?? []), level] });
      });
    });
  });
  // Longest first, so "carry out" wins over any shorter verb it starts with.
  const alternation = [...index.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  const anywhere = new RegExp(`\\b(?:${alternation})\\b`, "gi");
  const atStart = new RegExp(`^(?:${alternation})\\b`, "i");

  const scan = (text: string): VerbMatch[] => {
    const seen = new Set<string>();
    const matches: VerbMatch[] = [];
    [...normaliseText(text).matchAll(anywhere)].forEach((m) => {
      const known = index.get(m[0].toLowerCase());
      if (known && !seen.has(known.entry.lemma)) {
        seen.add(known.entry.lemma);
        matches.push({ verb: known.entry.lemma, levels: known.levels });
      }
    });
    return matches;
  };

  const emphasisFor = (level: number): Emphasis | null => alignment.emphasis[level] ?? null;

  const strategy: GuidanceStrategy = {
    taxonomy,
    framework,
    scan,
    classify: (text) => scan(leadingVerbClause(text, atStart)),
    emphasisFor,
    suggest: (level) => {
      const emphasis = emphasisFor(level);
      const ids = emphasis ? [...emphasis.primary, ...emphasis.secondary] : [];
      const verbs = ids.flatMap((id) => taxonomy.levels.find((l) => l.id === id)?.verbs ?? []);
      return verbs.filter((v, i) => verbs.findIndex((w) => w.lemma === v.lemma) === i);
    },
    suitability: (lemma, level) => {
      const emphasis = emphasisFor(level);
      const known = index.get(lemma.toLowerCase());
      if (!emphasis || !known) {
        return null;
      }
      const ids = known.levels.map((l) => l.id);
      if (ids.some((id) => emphasis.primary.includes(id))) {
        return "recommended";
      }
      return ids.some((id) => emphasis.secondary.includes(id)) ? "possible" : "unlikely";
    },
    Panel: (props) => createElement(GuidancePanel, { ...props, strategy }),
  };
  return strategy;
}
